import { useRef, useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Claim, ClaimType, Product } from "../api/models"
import { codeClaimsThunk } from "../redux/claimsSlice"
import { redeemCodeThunk } from "../redux/redemptionsSlice"
import { AppDispatch } from "../redux/store"
import { CRC } from 'crc-full'
import { useSession } from "../auth/session"
import * as zbase32 from 'zbase32';

// The redemption wizard (enter → review → done) as the topmost card of the
// License Codes page — it replaced the app-wide redeem modal. Other pages
// send users here with `redeemCodeState(code)` as router state to prefill a
// code (the Dashboard card's drop zone); the page clears that state on
// arrival. A successful redemption lands at the head of the page's list
// through the redemptions slice, so the card only needs to reset itself.
export type RedeemCodeState = { redeemCode: string }
export const redeemCodeState = (code: string): RedeemCodeState => ({ redeemCode: code })

function isKeyCrcOk(keyBase32: string): boolean {
  // DECODING:
  // 1. take 16 z-base32 characters key and decode to binary, getting 80 bits key
  // 2. store the last 8 bits as a checksum
  // 3. zero last 8 bits
  // 4. compute CRC8 of that number
  // 5. compare with the checksum

  const keyBase32Clean = keyBase32.trim().replace(/-/g, "").substring(4)
  if (keyBase32Clean.length !== 16) return false
  try {
    const keyBin = zbase32.decode(keyBase32Clean)
    const checksumBin: number = keyBin[keyBin.length - 1]
    keyBin[keyBin.length - 1] = 0
    const keyBuffer: number[] = Array.from(keyBin)
    const computedChecksum = CRC.default("CRC8_CDMA2000")!.compute(keyBuffer)
    return checksumBin === computedChecksum
  } catch (err) {
    console.log(err)
    return false
  }
}

// ── Constants ───────────────────────────────────────

const SEGMENT_COUNT = 5
const SEGMENT_LENGTH = 4
const ZBASE32 = "ybndrfg8ejkmcpqxot1uwisza345h769"
const ZBASE32_RE = /^[ybndrfg8ejkmcpqxot1uwisza345h769]{1,4}$/
const PREFIX_RE = /^[a-zA-Z0-9]{1,4}$/

type Step = "enter" | "review" | "success"

const stepLabels: { key: Step; label: string }[] = [
  { key: "enter", label: "Enter" },
  { key: "review", label: "Review" },
  { key: "success", label: "Done" },
]

function isValidChar(char: string, segmentIndex: number): boolean {
  if (segmentIndex === 0) return /^[a-zA-Z0-9]$/.test(char)
  return ZBASE32.includes(char.toLowerCase())
}

function filterSegment(raw: string, segmentIndex: number): string {
  return raw
    .split("")
    .filter((ch) => isValidChar(ch, segmentIndex))
    .slice(0, SEGMENT_LENGTH)
    .join("")
}

function normalizeSegment(value: string, segmentIndex: number): string {
  return segmentIndex === 0 ? value.toUpperCase() : value.toLowerCase()
}

// Splits a pasted/dropped code into normalized segments.
function splitCode(code: string): string[] {
  const stripped = code.trim().replace(/-/g, "")
  const next: string[] = []
  for (let i = 0; i < SEGMENT_COUNT; i++) {
    const chunk = stripped.slice(i * SEGMENT_LENGTH, (i + 1) * SEGMENT_LENGTH)
    next.push(normalizeSegment(filterSegment(chunk, i), i))
  }
  return next
}

// ── Card component ──────────────────────────────────

export default function RedeemCodeCard({ initialCode }: { initialCode?: string }) {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dispatch = useDispatch<AppDispatch>()
  const [step, setStep] = useState<Step>("enter")
  const [segments, setSegments] = useState<string[]>(() => initialCode ? splitCode(initialCode) : Array(SEGMENT_COUNT).fill(""))
  const [error, setError] = useState("")
  const [claims, setClaims] = useState<Claim[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [rewardPoints, setRewardPoints] = useState(0)
  const [loading, setLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const stepIndex = stepLabels.findIndex((s) => s.key === step)
  const fullCode = segments.join("-")
  const isFull = segments.every((s) => s.length === SEGMENT_LENGTH)

  const focusSegment = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  // A code handed in from elsewhere (Dashboard drop zone): fill it in and put
  // the caret where typing should continue.
  useEffect(() => {
    if (!initialCode) return
    const next = splitCode(initialCode)
    setStep("enter")
    setSegments(next)
    setError("")
    const lastFilled = next.reduce((last, s, i) => (s.length > 0 ? i : last), 0)
    const target = next[lastFilled]?.length === SEGMENT_LENGTH && lastFilled < SEGMENT_COUNT - 1
      ? lastFilled + 1
      : lastFilled
    setTimeout(() => focusSegment(target), 0)
  }, [initialCode, focusSegment])

  useEffect(() => {
    if (!isFull) return
    if (!isKeyCrcOk(fullCode)) {
      setError("Invalid code. Please check and try again.")
    } else {
      setError("")
    }
  }, [isFull, fullCode])

  const handleChange = (index: number, raw: string) => {
    const filtered = normalizeSegment(filterSegment(raw, index), index)
    setSegments((prev) => {
      const next = [...prev]
      next[index] = filtered
      return next
    })
    setError("")
    if (filtered.length === SEGMENT_LENGTH && index < SEGMENT_COUNT - 1) {
      focusSegment(index + 1)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && segments[index] === "" && index > 0) {
      e.preventDefault()
      focusSegment(index - 1)
    }
    if (e.key === "ArrowLeft" && e.currentTarget.selectionStart === 0 && index > 0) {
      e.preventDefault()
      focusSegment(index - 1)
    }
    if (e.key === "ArrowRight" && e.currentTarget.selectionStart === segments[index].length && index < SEGMENT_COUNT - 1) {
      e.preventDefault()
      focusSegment(index + 1)
    }
    if (e.key === "Enter") {
      handleSubmit()
    }
  }

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault()
    const stripped = e.clipboardData.getData("text").trim().replace(/-/g, "")
    let pos = 0
    const next = [...segments]
    for (let i = index; i < SEGMENT_COUNT && pos < stripped.length; i++) {
      const chunk = stripped.slice(pos, pos + SEGMENT_LENGTH)
      next[i] = normalizeSegment(filterSegment(chunk, i), i)
      pos += SEGMENT_LENGTH
    }
    setSegments(next)
    setError("")
    const lastFilled = next.reduce((last, s, i) => (i >= index && s.length > 0 ? i : last), index)
    const target = next[lastFilled]?.length === SEGMENT_LENGTH && lastFilled < SEGMENT_COUNT - 1
      ? lastFilled + 1
      : lastFilled
    setTimeout(() => focusSegment(target), 0)
  }

  const handleSubmit = async () => {
    if (!isFull) {
      setError("Please enter the complete code.")
      return
    }
    if (!PREFIX_RE.test(segments[0])) {
      setError("Invalid prefix format.")
      return
    }
    for (let i = 1; i < SEGMENT_COUNT; i++) {
      if (!ZBASE32_RE.test(segments[i])) {
        setError(`Segment ${i + 1} contains invalid characters.`)
        return
      }
    }
    if (!isKeyCrcOk(fullCode)) {
      setError("Invalid code. Please check and try again.")
      return
    }
    setLoading(true)
    setError("")
    try {
      const result = await dispatch(codeClaimsThunk({ redemptionCode: fullCode, accessToken })).unwrap()
      setClaims(result.claims)
      setProducts(result.products)
      setRewardPoints(result.rewardPoints ?? 0)
      setStep("review")
    } catch (err: any) {
      setError(err?.message || "Failed to look up code. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    setLoading(true)
    setError("")
    try {
      await dispatch(redeemCodeThunk({ code: fullCode, accessToken })).unwrap()
      setStep("success")
    } catch (err: any) {
      setError(err?.message || "Failed to redeem code. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleRedeemAnother = () => {
    setStep("enter")
    setSegments(Array(SEGMENT_COUNT).fill(""))
    setError("")
    setClaims([])
    setProducts([])
    setRewardPoints(0)
    setTimeout(() => focusSegment(0), 0)
  }

  const placeholders = ["XXXX", "aaaa", "bbbb", "cccc", "dddd"]

  return (
    <div className="card redeem-card">
      <h2 className="card-title">Redeem Code</h2>
      <div className="redeem-card-body">
        <div className="wizard-steps">
          {stepLabels.map((s, i) => (
            <span key={s.key} style={{ display: "contents" }}>
              {i > 0 && (
                <div className={`wizard-line${i <= stepIndex ? " wizard-line-done" : ""}`} />
              )}
              <div className={`wizard-node${i === stepIndex ? (i === stepLabels.length - 1 ? " wizard-node-done" : " wizard-node-active") : i < stepIndex ? " wizard-node-done" : ""
                }`}>
                <div className="wizard-dot" />
                <span className="wizard-label">{s.label}</span>
              </div>
            </span>
          ))}
        </div>

        {step === "enter" && (
          <div>
            <div className="code-input-group">
              {segments.map((seg, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center" }}>
                  {i > 0 && <span className="code-dash">-</span>}
                  <input
                    ref={(el) => { inputRefs.current[i] = el }}
                    className={`code-segment${error ? " code-segment-error" : ""}`}
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={SEGMENT_LENGTH}
                    placeholder={placeholders[i]}
                    value={seg}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={(e) => handlePaste(i, e)}
                  />
                </span>
              ))}
            </div>
            <p className="redeem-error" style={{ visibility: error ? "visible" : "hidden" }}>{error || " "}</p>
            <div className="redeem-actions">
              {segments.some(s => s.length > 0) && (
                <button className="btn-downgrade" onClick={() => { setSegments(Array(SEGMENT_COUNT).fill("")); setError(""); setTimeout(() => focusSegment(0), 0) }}>Clear</button>
              )}
              <button onClick={handleSubmit} disabled={!isFull || !!error || loading}>{loading ? "Loading…" : "Next"}</button>
            </div>
          </div>
        )}

        {step === "review" && claims.length > 0 && (
          <div>
            <h3 className="redeem-bundle-title">Code Contents</h3>
            <div className="redeem-summary">
              {claims.filter(c => c.type === ClaimType.License).map(c => {
                const product = products.find(p => p.id === c.productId)
                return (
                  <div className="redeem-summary-row" key={c.id}>
                    <span>License</span>
                    <span className="redeem-summary-value">{product?.name ?? c.productId}</span>
                  </div>
                )
              })}
              {claims.filter(c => c.type === ClaimType.Topup).map(c => (
                <div className="redeem-summary-row" key={c.id}>
                  <span>Credits</span>
                  <span className="redeem-summary-value">+{((c.amount ?? 0) / 1e9).toFixed(2)}</span>
                </div>
              ))}
              {rewardPoints > 0 && (
                <div className="redeem-summary-row">
                  <span>Loyalty Points</span>
                  <span className="redeem-summary-value">+{rewardPoints}</span>
                </div>
              )}
            </div>
            <p className="redeem-error" style={{ visibility: error ? "visible" : "hidden" }}>{error || " "}</p>
            <div className="redeem-actions">
              <button className="btn-downgrade" onClick={handleRedeemAnother} disabled={loading}>Back</button>
              <button onClick={handleConfirm} disabled={loading}>{loading ? "Redeeming…" : "Redeem"}</button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="redeem-success">
            <span className="redeem-success-icon">&#10003;</span>
            <h3 className="redeem-bundle-title">Redemption Complete!</h3>
            <p>The code has been applied to your account.</p>
            {claims.filter(c => c.type === ClaimType.License).length > 0 && (
              <p>Licenses: {claims.filter(c => c.type === ClaimType.License).map(c => {
                const product = products.find(p => p.id === c.productId)
                return product?.name ?? c.productId
              }).join(", ")}</p>
            )}
            {claims.filter(c => c.type === ClaimType.Topup).map(c => (
              <p key={c.id}>Credits: +{((c.amount ?? 0) / 1e9).toFixed(2)}</p>
            ))}
            {rewardPoints > 0 && (
              <p>Loyalty Points: +{rewardPoints}</p>
            )}
            <div className="redeem-success-links">
              {claims.some(c => c.type === ClaimType.License) && <Link to="/licenses">View Licenses</Link>}
              {claims.some(c => c.type === ClaimType.Topup) && <Link to="/credits">View Credits</Link>}
              {rewardPoints > 0 && <Link to="/loyalty">View Loyalty Points</Link>}
            </div>
            <div className="redeem-actions">
              <button onClick={handleRedeemAnother}>Redeem another code</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
